import { h, Fragment } from 'preact';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useEffect, useState, useRef } from 'preact/hooks';
import { Navigation, Mousewheel } from 'swiper';
import AngleRightIcon from 'utils/icon/icon-angle-right.svg';
import clsx from 'clsx';

const Swatch = ({
  product,
  currentVariant,
  variant,
  productId,
  handleVariantChange,
  condensed,
  template,
  prefix = 'swatch',
}) => {
  const colorOption = variant.selectedOptions.find((option) => option.name.toLowerCase() === 'color');

  if (!colorOption) return null;

  // Swatch is enabled if any variant with this color is available
  let available = true;
  if (template === 'pdp') {
    // On PDP, show as available only if any variant with this color is available
    available = product.variants.some((productVariant) =>
      productVariant.selectedOptions.some(
        (option) =>
          option.name.toLowerCase() === 'color' &&
          option.value?.toLowerCase() === colorOption.value?.toLowerCase() &&
          productVariant.availableForSale,
      ),
    );
  }

  const getHexCodeWithHash = (hexCode) => (hexCode?.startsWith('#') ? hexCode : `#${hexCode}`);
  const bgColorVar = getHexCodeWithHash(variant.hexCode || '#ccc');
  const className = condensed ? 'h-25 w-25 min-w-25' : 'h-[30px] w-[30px] min-w-[30px]';
  // Clean handle: remove leading '/products/' if present
  let cleanHandle = product.handle;
  if (typeof cleanHandle === 'string' && cleanHandle.startsWith('/products/')) {
    cleanHandle = cleanHandle.replace(/^\/products\//, '');
  }

  // Get variant id (if it contains '/ProductVariant/')
  let variantId = variant.id;
  if (typeof variantId === 'string' && variantId.includes('/ProductVariant/')) {
    [, variantId] = variantId.split('/ProductVariant/');
  }

  // DISABLED - KEEPING FOR REFERENCE
  // const handleSwatchClick = (e) => {
  //   e.preventDefault();
  //   if (!available) return;
  //   if (template === 'card' || template === 'collection') {
  //     // On product card, go to PDP for selected variant
  //     const url = `/products/${cleanHandle}${variantId ? `?variant=${variantId}` : ''}`;
  //     window.location.href = url;
  //   } else if (template === 'pdp') {
  //     // On PDP, just load the variant
  //     const input = document.getElementById(`${prefix}-${variantId}-${template}`);
  //     if (input && !input.disabled) {
  //       input.checked = true;
  //       input.dispatchEvent(new Event('change', { bubbles: true }));
  //     }
  //   }
  // };

  const handleSwatchClick = (e) => {
    if (template === 'card' || template === 'collection' || template === 'pdp') {
      handleVariantChange(e, variantId, colorOption);
    }
  };

  // DISABLED - KEEPING FOR REFERENCE
  // const handleSwatchHover = (e) => {
  //   if (template === 'card' || template === 'collection') {
  //     // On product card, hovering updates the card variant
  //     handleVariantChange(e, variantId, colorOption);
  //   }
  // };

  return (
    <div
      key={variant.id}
      className={clsx(
        'relative flex cursor-pointer items-center rounded-full border border-transparent',
        {
          'ring-1 ring-grey-5': bgColorVar.toLowerCase() === '#ffffff',
          '!cursor-default after:absolute after:z-10 after:w-1 after:rotate-[45deg] after:bg-black after:content-[""]':
            !available,
          'after:-top-[3px] after:left-[11px] after:h-[30px]': condensed,
          'after:-top-[2px] after:left-[14px] after:h-[34px]': !condensed,
        },
        className,
      )}
      style={{ backgroundColor: bgColorVar }}
      onClick={handleSwatchClick}
      onTouchStart={handleSwatchClick}
    >
      <input
        key={`${colorOption.value}-${currentVariant?.id}`}
        checked={
          currentVariant?.selectedOptions?.find((opt) => opt.name.toLowerCase() === 'color')?.value?.toLowerCase() ===
          colorOption?.value?.toLowerCase()
        }
        id={`${prefix}-${variantId}-${template}`}
        onChange={(e) => handleVariantChange(e, variantId, colorOption)}
        type="radio"
        value={colorOption.value}
        name={`${prefix}-${productId}-color`}
        data-option-name="Color"
        className={`swatch ${className} ${!available ? 'disabled' : ''}`}
        disabled={!available}
        aria-label={`Color ${colorOption.value}${!available ? ' - Out of stock' : ''}`}
        aria-disabled={!available}
      />
      <label
        htmlFor={`${prefix}-${variantId}-${template}`}
        aria-hidden="true"
      >
        <span className="sr-only">
          {colorOption.value}
          {!available && ' - Out of stock'}
        </span>
      </label>
    </div>
  );
};

const Swatches = ({
  product,
  currentVariant,
  handleVariantChange,
  condensed,
  isDesktop = null,
  template,
  prefix = '',
  filteredVariants = [],
}) => {
  const productId = product.idClean;
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  let colorOptionIndex = -1;
  if (Array.isArray(product.options)) {
    colorOptionIndex = product.options.findIndex(
      (o) => o && o.name && typeof o.name === 'string' && o.name.toLowerCase().includes('color'),
    );
  }
  // If product.options is missing or colorOptionIndex < 0, but variants have selectedOptions, default to 0
  if (
    colorOptionIndex < 0 &&
    Array.isArray(product.variants) &&
    product.variants.length > 0 &&
    Array.isArray(product.variants[0].selectedOptions)
  ) {
    colorOptionIndex = 0;
  }

  // Defensive: If colorOptionIndex is -1, never access selectedOptions[-1]

  const getUniqueColorVariants = (variants) => {
    const seen = new Set();
    return variants.filter((variant) => {
      const key = `${variant.selectedOptions?.[colorOptionIndex]?.value || ''}_${variant.hexCode || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const [colorVariants, setColorVariants] = useState(() =>
    Array.isArray(product?.variants) ? getUniqueColorVariants(product.variants) : [],
  );

  useEffect(() => {
    setColorVariants(Array.isArray(filteredVariants) ? getUniqueColorVariants(filteredVariants) : []);
  }, [filteredVariants, colorOptionIndex]);

  if (colorOptionIndex < 0) {
    return (
      <div className="mb-12 h-25">
        <div
          className={`relative flex h-25 w-25 cursor-pointer items-center
            rounded-full border border-transparent ring-1 ring-grey-5`}
        >
          <div
            className={`swatch bg-putty ring-optimistic-blue hover:!bg-putty h-25 w-25
              rounded-full opacity-100 ring-1 ring-offset-[1px]`}
          ></div>
        </div>
      </div>
    );
  }

  if (colorVariants.length < 1) return null;

  const showSlider = (isDesktop ? colorVariants.length > 6 : colorVariants.length > 5) && condensed;

  return (
    <div
      className={condensed ? 'relative mb-12 flex max-w-[220px] items-center justify-start pr-15' : 'pl-2'}
      style={showSlider ? { justifyContent: 'flex-start' } : {}}
    >
      {showSlider && (
        <>
          <button
            ref={prevRef}
            className="absolute left-0 z-10 hidden s:left-0 s:right-auto"
            aria-label="Previous"
            tabIndex={-1}
            style={{ display: 'none' }}
          />
          <button
            ref={nextRef}
            className="absolute right-0 z-10 s:left-auto s:right-0"
            aria-label="Next"
          >
            <AngleRightIcon />
          </button>
        </>
      )}
      {showSlider ? (
        <Swiper
          className="!ml-0 h-[29px] !pr-2"
          modules={[Navigation, Mousewheel]}
          slidesPerView={isDesktop ? 6 : 5}
          navigation={{
            nextEl: nextRef.current,
            prevEl: prevRef.current,
          }}
          mousewheel={true}
          onInit={(swiperInstance) => {
            const swiper = swiperInstance;
            swiper.params.navigation.nextEl = nextRef.current;
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.navigation.init();
            swiper.navigation.update();
          }}
        >
          {colorVariants.map((variant) => (
            <SwiperSlide
              key={variant.id}
              className="!flex min-h-25 items-center justify-center"
            >
              <Swatch
                product={product}
                variant={variant}
                currentVariant={currentVariant}
                productId={productId}
                handleVariantChange={handleVariantChange}
                condensed={condensed}
                template={template}
                prefix={prefix}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <div className={`${condensed ? 'flex gap-8' : '-mt-4 gap-15 p-4'} flex flex-wrap items-center`}>
          {colorVariants.map((variant) => (
            <Fragment
              key={variant.selectedOptions?.find((opt) => opt.name.toLowerCase() === 'color')?.value || variant.id}
            >
              <Swatch
                product={product}
                currentVariant={currentVariant}
                variant={variant}
                productId={productId}
                handleVariantChange={handleVariantChange}
                condensed={condensed}
                template={template}
                prefix={prefix}
              />
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default Swatches;
